<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-left mb-4 mb-sm-0">
        <h3 class="page-title">
          <template v-if="isEdit">Edytuj produkt</template>
          <template v-else>Nowy produkt</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end"
      >
        <d-button
          theme="primary"
          outline
          @click.prevent="$router.push('/products')"
        >
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">done</i>
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-row>
        <d-col cols="12">
          <d-card class="mb-4 is-over">
            <d-card-body>
              <div
                style="
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-bottom: 1rem;
                "
              >
                <h4 style="margin: 0">Szczegóły produktu</h4>

                <div style="display: flex; gap: 0.5rem">
                  <d-button
                    v-if="isEdit"
                    theme="warning"
                    type="button"
                    @click.prevent="showDuplicateModal"
                  >
                    <i class="material-icons">content_copy</i>
                    Duplikuj
                  </d-button>

                  <d-dropdown
                    v-if="isEdit"
                    text="Pobierz PDF"
                    theme="warning"
                    outline
                  >
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadPDF('a4')"
                    >
                      <template v-if="!state.downloadA4PdfInProgress">
                        <i class="material-icons">cloud_download</i>
                        Format A4
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadPDF('b5')"
                    >
                      <template v-if="!state.downloadB5PdfInProgress">
                        <i class="material-icons">cloud_download</i>
                        Format B5
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                  </d-dropdown>

                  <d-dropdown
                    v-if="isEdit"
                    text="Pobierz obraz"
                    theme="warning"
                    outline
                  >
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadImage('a4', 'png')"
                    >
                      <template v-if="!state.downloadA4PngInProgress">
                        <i class="material-icons">image</i>
                        PNG A4
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadImage('a4', 'jpg')"
                    >
                      <template v-if="!state.downloadA4JpgInProgress">
                        <i class="material-icons">image</i>
                        JPG A4
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadImage('b5', 'png')"
                    >
                      <template v-if="!state.downloadB5PngInProgress">
                        <i class="material-icons">image</i>
                        PNG B5
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                    <a
                      href="#"
                      class="dropdown-item"
                      @click.prevent="downloadImage('b5', 'jpg')"
                    >
                      <template v-if="!state.downloadB5JpgInProgress">
                        <i class="material-icons">image</i>
                        JPG B5
                      </template>
                      <template v-else>
                        <div
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </template>
                    </a>
                  </d-dropdown>
                </div>
              </div>

              <validation-observer
                v-if="allDataLoaded"
                ref="product-form"
                tag="form"
              >
                <d-row>
                  <d-col cols="6">
                    <div class="form-group">
                      <label for="name"> Nazwa: </label>

                      <validation-provider
                        rules="required"
                        name="name"
                        v-model="data.name"
                        v-slot="{ errors }"
                      >
                        <d-input
                          :state="errors.length ? 'invalid' : null"
                          v-model="data.name"
                        />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>

                    <div class="form-group">
                      <label for="name"> Kategoria: </label>

                      <validation-provider
                        rules="required"
                        name="type_id"
                        v-model="data.type_id"
                        v-slot="{ errors }"
                      >
                        <div
                          :class="{
                            'form-control is-select': true,
                            'is-invalid': errors.length,
                          }"
                        >
                          <v-select
                            :options="typeOptions"
                            v-model="data.type_id"
                            :searchable="false"
                            :clearable="false"
                            placeholder="Wybierz typ"
                            :reduce="(type) => type.id"
                            label="label"
                            @input="onTypeChange"
                          >
                          </v-select>
                        </div>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>

                    <div v-if="subcategoryOptions.length > 0">
                      <label for="subcategory_id"> Podkategoria: </label>

                      <div class="form-control is-select">
                        <v-select
                          :options="subcategoryOptions"
                          v-model="selectedSubcategory"
                          :searchable="false"
                          :clearable="true"
                          @input="onSubcategoryChange"
                          placeholder="Wybierz podkategorię (opcjonalnie)"
                          label="name"
                        >
                        </v-select>
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="name"> Opakowanie: </label>

                      <validation-provider
                        rules="required"
                        name="package_id"
                        v-model="data.package_id"
                        v-slot="{ errors }"
                      >
                        <div
                          :class="{
                            'form-control is-select': true,
                            'is-invalid': errors.length,
                          }"
                        >
                          <v-select
                            :options="packageOptions"
                            v-model="data.package_id"
                            placeholder="Wybierz opakowanie"
                            :reduce="(packageItem) => packageItem.id"
                            label="label"
                          >
                          </v-select>
                        </div>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>

                    <div class="form-group">
                      <label for="status">
                        Status:
                        <small>(opcjonalnie)</small>
                      </label>

                      <div class="form-control is-select">
                        <v-select
                          :options="statusOptions"
                          v-model="data.status_id"
                          placeholder="Wybierz status"
                          :reduce="(status) => status.id"
                          label="label"
                          :clearable="true"
                        >
                          <template #option="option">
                            <span 
                              class="status-badge"
                              :style="{ backgroundColor: option.color }">
                              {{ option.label }}
                            </span>
                          </template>
                          <template #selected-option="option">
                            <span 
                              class="status-badge"
                              :style="{ backgroundColor: option.color }">
                              {{ option.label }}
                            </span>
                          </template>
                        </v-select>
                      </div>
                    </div>

                    <div class="form-group">
                      <toggle-switch v-model="data.show_package">
                        Pokaż opakowanie na podglądzie
                      </toggle-switch>
                    </div>
                    
                    <div class="form-group pt-4" v-if="isEdit">
                        <d-button
                            v-if="!data.archived_at" 
                            theme="warning" 
                            @click.prevent="promptArchiveProduct">
                            Przenieś do Archiwum
                        </d-button>
                         <d-button
                            v-else 
                            theme="success" 
                            @click.prevent="promptRestoreProduct">
                            Przywróć z Archiwum
                        </d-button>
                    </div>
                  </d-col>

                  <d-col cols="6">
                    <div class="form-group">
                      <label for="project_name"> Nazwa projektu </label>
                      <d-input
                        id="project_name"
                        v-model="data.project_name"
                        placeholder="Wprowadź nazwę projektu"
                      />
                    </div>
                    <label for="image">
                      Zdjęcie
                      <small>Zdjęcie finalnego produktu (opcjonalne)</small>
                    </label>

                    <div v-if="data.image_url" class="product-image-wrapper">
                      <img :src="data.image_url" />
                    </div>

                    <div v-if="data.image_url" class="text-center">
                      <d-button
                        theme="danger"
                        outline
                        @click.prevent="removeImage()"
                      >
                        Zmień zdjęcie
                      </d-button>
                    </div>

                    <file-input
                      v-if="!data.image_url"
                      :onChange="setImage"
                      id="image"
                    />
                  </d-col>
                </d-row>
              </validation-observer>

              <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert v-if="loadError" show theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link"
                  >Odśwież stronę</a
                >
                aby spróbować ponownie.
              </d-alert>
            </d-card-body>
          </d-card>
        </d-col>
      </d-row>

      <d-row class="mt-4 mb-4">
        <d-col :cols="useDrinkEditor ? 5 : 4" class="sticky-ui-wrapper">
          <div class="sticky-ui d-flex pt-2 pb-2 pl-2 pr-2">
            <d-button-group class="mr-2">
              <d-button
                v-if="!useDrinkEditor"
                theme="primary"
                :outline="editView !== 'blocks'"
                @click.prevent="setEditView('blocks')"
              >
                Bloki ({{ data.blocks.length }})
              </d-button>
              <d-button
                v-if="useDrinkEditor"
                theme="primary"
                :outline="editView !== 'fluids'"
                @click.prevent="setEditView('fluids')"
              >
                Składniki ({{ data.fluids.length }})
              </d-button>
              <d-button
                v-if="useDrinkEditor"
                theme="primary"
                :outline="editView !== 'addons'"
                @click.prevent="setEditView('addons')"
              >
                Dodatki ({{ data.addons.length }})
              </d-button>
              <d-button
                theme="primary"
                :outline="editView !== 'texts'"
                @click.prevent="setEditView('texts')"
              >
                Teksty ({{ data.layers.length }})
              </d-button>
              <d-button
                v-if="useDrinkEditor"
                theme="primary"
                :outline="editView !== 'steps'"
                @click.prevent="setEditView('steps')"
              >
                Kroki ({{ data.steps.length }})
              </d-button>
            </d-button-group>

            <d-button
              v-if="editView === 'texts'"
              theme="secondary"
              @click.prevent="addTextLayer"
              class="ml-auto"
              :disabled="!data.blocks.length && !data.fluids.length"
            >
              Dodaj tekst
            </d-button>

            <d-button
              v-if="editView === 'fluids'"
              theme="secondary"
              @click.prevent="addFluidLayer"
              class="ml-auto"
            >
              Dodaj składnik
            </d-button>

            <d-button
              v-if="editView === 'addons'"
              theme="secondary"
              @click.prevent="addAddonLayer"
              class="ml-auto"
            >
              Dodaj dodatek
            </d-button>

            <d-button
              v-if="editView === 'steps'"
              theme="secondary"
              @click.prevent="addStep"
              class="ml-auto"
            >
              Dodaj krok
            </d-button>

            <d-dropdown
              v-if="editView === 'blocks'"
              ref="addBlockDropdown"
              text="Dodaj blok"
              theme="success"
              outline
              class="ml-auto"
            >
              <a
                href="#"
                class="dropdown-item"
                @click.prevent="addIngredientAndClose"
              >
                <i class="material-icons">category</i> Składnik
              </a>
              <a
                href="#"
                class="dropdown-item"
                @click.prevent="addSpacerAndClose"
              >
                <i class="material-icons">format_line_spacing</i> Odstęp
              </a>
            </d-dropdown>
          </div>

          <d-alert
            v-if="editView === 'blocks' && !data.blocks.length"
            theme="light"
            show
            class="mt-3"
          >
            Brak bloków dodanych do tego produktu
          </d-alert>

          <d-alert
            v-if="editView === 'fluids' && !data.fluids.length"
            theme="light"
            show
            class="mt-3"
          >
            Brak składników dodanych do tego napoju
          </d-alert>

          <d-alert
            v-if="editView === 'addons' && !data.addons.length"
            theme="light"
            show
            class="mt-3"
          >
            Brak dodatków dodanych do tego napoju
          </d-alert>

          <d-alert
            v-if="editView === 'steps' && !data.steps.length"
            theme="light"
            show
            class="mt-3"
          >
            Brak kroków instrukcji dodanych do tego napoju
          </d-alert>

          <d-alert
            v-if="editView === 'texts' && !data.layers.length"
            theme="light"
            show
            class="mt-3"
          >
            <template v-if="useDrinkEditor">
              <template v-if="!data.fluids.length"
                >Dodawanie tekstów będzie możliwe po dodaniu
                składników</template
              >
              <template v-if="data.fluids.length"
                >Brak tekstów dodanych do tego napoju</template
              >
            </template>
            <template v-else>
              <template v-if="!data.blocks.length"
                >Dodawanie tekstów będzie możliwe po dodaniu bloków</template
              >
              <template v-if="data.blocks.length"
                >Brak tekstów dodanych do tego produktu</template
              >
            </template>
          </d-alert>

          <draggable
            v-if="data.blocks && data.blocks.length && editView === 'blocks'"
            v-model="data.blocks"
            tag="ol"
            item-key="id"
            class="blocks-list mt-3"
          >
            <template #item="{ element: block, index }">
              <li
                v-if="data.blocks[index]"
                @click="toggleBlockSelection(block.id)"
                :class="{
                  block: true,
                  'block-small': block.space === 'small',
                  'block-medium': block.space === 'medium',
                  'block-big':
                    block.type === 'ingredient' && !!isBigBlock[block.id],
                  'is-selected': state.selectedBlock === block.id,
                }"
              >
                <d-card v-if="block.type === 'ingredient'">
                  <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                    <d-row class="block-values">
                      <div @click.stop class="form-control is-select">
                        <v-select
                          :options="ingredientsFood"
                          v-model="data.blocks[index].ingredient_id"
                          :searchable="true"
                          :clearable="false"
                          :reduce="(ingredient) => ingredient.id"
                          label="name"
                          placeholder="Wybierz składnik"
                          @input="resetIngredientParams(index)"
                        >
                        </v-select>
                      </div>
                    </d-row>

                    <d-row
                      :class="{
                        'mt-2 block-options': true,
                        'one-column': !ingredientIsCountable(index),
                      }"
                    >
                      <div
                        v-if="data.blocks[index].ingredient_id"
                        @click.stop
                        class="form-control is-select is-hidden"
                      >
                        <v-select
                          :options="
                            ingredientUnits(data.blocks[index].ingredient_id)
                          "
                          v-model="data.blocks[index].unit_id"
                          :searchable="false"
                          :clearable="false"
                          :disabled="
                            ingredientUnits(data.blocks[index].ingredient_id)
                              .length < 2
                          "
                          :reduce="(unit) => unit.id"
                          label="name"
                          placeholder="Wybierz"
                        >
                        </v-select>
                      </div>

                      <div
                        v-if="ingredientIsCountable(index)"
                        @click.stop
                        cols="4"
                        class="pl-0 pr-0 is-hidden"
                      >
                        <d-input-group @click.stop>
                          <d-input
                            @click.prevent.stop
                            type="number"
                            min="1"
                            max="8"
                            step="1"
                            v-model="data.blocks[index].quantity"
                          />
                          <template #append>
                            <d-input-group-text> szt. </d-input-group-text>
                          </template>
                        </d-input-group>
                      </div>
                    </d-row>

                    <div class="mt-2">
                      <d-input-group @click.stop>
                        <d-input
                          placeholder="Tekst do wyświetlenia"
                          v-model="block.label"
                          @click.stop
                        />
                        <d-input-group-addon v-if="!useDrinkEditor" append>
                          <div @click.stop class="form-control is-select is-small">
                            <v-select
                              :options="fontSizeOptions"
                              v-model="block.font_size"
                              :searchable="false"
                              :clearable="false"
                              :reduce="(size) => size.value"
                              label="text"
                              placeholder="24"
                            >
                            </v-select>
                          </div>
                        </d-input-group-addon>
                        <d-input-group-addon append>
                          <d-button-group>
                            <d-button
                              theme="primary"
                              :outline="block.label_position !== 'top'"
                              @click.stop.prevent="block.label_position = 'top'"
                            >
                              <i class="material-icons">vertical_align_top</i>
                            </d-button>
                            <d-button
                              theme="primary"
                              :outline="block.label_position !== 'bottom'"
                              @click.stop.prevent="
                                block.label_position = 'bottom'
                              "
                            >
                              <i class="material-icons"
                                >vertical_align_bottom</i
                              >
                            </d-button>
                          </d-button-group>
                        </d-input-group-addon>
                      </d-input-group>
                    </div>

                    <d-row class="mt-2 mb-1 block-options">
                      <toggle-switch
                        v-model="data.blocks[index].is_optional"
                        @click.stop
                      >
                        opcjonalny
                      </toggle-switch>

                      <toggle-switch
                        v-model="data.blocks[index].is_seasonal"
                        @click.stop
                      >
                        sezonowy
                      </toggle-switch>

                      <toggle-switch
                        v-model="data.blocks[index].is_underline"
                        @click.stop
                      >
                        podkreślenie
                      </toggle-switch>
                    </d-row>
                  </d-card-body>

                  <a
                    href="#delete"
                    class="block-remove text-white"
                    @click.stop.prevent="removeBlock(index)"
                  >
                    <i class="material-icons">delete</i>
                  </a>
                </d-card>

                <d-card v-if="block.type === 'spacer'">
                  <d-card-body class="pt-2 pb-2 pl-0 pr-0">
                    <d-row class="block-values">
                      <d-col cols="4"> Odstęp: </d-col>

                      <d-col cols="8">
                        <div @click.stop class="form-control is-select">
                          <v-select
                            :options="[
                              { text: 'Mały odstęp', value: 'small' },
                              { text: 'Średni odstęp', value: 'medium' },
                              { text: 'Duży odstęp', value: 'big' },
                            ]"
                            v-model="data.blocks[index].space"
                            :searchable="false"
                            :clearable="false"
                            :reduce="(space) => space.value"
                            label="text"
                            placeholder="Wybierz rozmiar"
                          >
                          </v-select>
                        </div>
                      </d-col>
                    </d-row>
                  </d-card-body>

                  <a
                    href="#delete"
                    class="block-remove text-white"
                    @click.stop.prevent="removeBlock(index)"
                  >
                    <i class="material-icons">delete</i>
                  </a>
                </d-card>
              </li>
            </template>
          </draggable>

          <div
            v-if="editView === 'fluids' && data.use_topping_ingredient"
            :key="'topping-' + data.type_id"
            class="blocks-list blocks-list-special blocks-list-topping"
          >
            <div class="block mr-2">
              <d-card>
                <d-card-body class="p-3">
                  <div @click.stop class="form-control is-select">
                    <v-select
                      :options="ingredientsToppings"
                      v-model="data.topping_ingredient[0].ingredient_id"
                      :searchable="true"
                      :clearable="true"
                      :reduce="(ingredient) => ingredient.id"
                      label="name"
                      placeholder="Wybierz wierzchnią ozdobę"
                    >
                    </v-select>
                  </div>

                  <div class="mt-2">
                    <d-input
                      v-model="data.topping_ingredient[0].amount"
                      placeholder="Ilość"
                    />
                  </div>

                  <div class="mt-2">
                    <d-textarea
                      v-model="data.topping_ingredient[0].comment"
                      placeholder="Dodatkowy komentarz"
                      :rows="3"
                    />
                  </div>
                </d-card-body>
              </d-card>
            </div>

            <div class="block ml-2">
              <d-card>
                <d-card-body class="p-3">
                  <div @click.stop class="form-control is-select">
                    <v-select
                      :options="ingredientsToppings"
                      v-model="data.topping_ingredient[1].ingredient_id"
                      :searchable="true"
                      :clearable="true"
                      :reduce="(ingredient) => ingredient.id"
                      label="name"
                      placeholder="Wybierz wierzchnią ozdobę"
                    >
                    </v-select>
                  </div>

                  <div class="mt-2">
                    <d-input
                      v-model="data.topping_ingredient[1].amount"
                      placeholder="Ilość"
                    />
                  </div>

                  <div class="mt-2">
                    <d-textarea
                      v-model="data.topping_ingredient[1].comment"
                      placeholder="Dodatkowy komentarz"
                      :rows="3"
                    />
                  </div>
                </d-card-body>
              </d-card>
            </div>
          </div>

          <div
            v-if="editView === 'fluids' && data.use_cover_ingredient"
            :key="'cover-' + data.type_id"
            class="blocks-list blocks-list-special blocks-list-cover"
          >
            <div class="block">
              <d-card>
                <d-card-body class="p-3">
                  <div @click.stop class="form-control is-select">
                    <v-select
                      :options="ingredientsCovers"
                      v-model="data.cover_ingredient.ingredient_id"
                      :searchable="true"
                      :clearable="false"
                      :reduce="(ingredient) => ingredient.id"
                      label="name"
                      placeholder="Wybierz wierzchni składnik"
                    >
                    </v-select>
                  </div>

                  <div @click.stop class="w-100 mt-2">
                    <d-input
                      v-model="data.cover_ingredient.comment"
                      placeholder="Dodatkowy komentarz"
                    >
                    </d-input>
                  </div>
                </d-card-body>
              </d-card>
            </div>
          </div>

          <draggable
            v-if="data.fluids && data.fluids.length && editView === 'fluids'"
            v-model="data.fluids"
            tag="ol"
            item-key="id"
            class="blocks-list mt-3"
          >
            <template #item="{ element: fluid, index }">
              <li
                v-if="data.fluids[index]"
                @click="toggleBlockSelection(fluid.id)"
                :class="{
                  block: true,
                  'block-fluid': true,
                  'is-selected': state.selectedBlock === fluid.id,
                }"
              >
                <d-card>
                  <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                    <div class="block-values block-fluid-values d-flex">
                      <div @click.stop class="form-control is-select">
                        <v-select
                          :options="ingredientsFluids"
                          v-model="data.fluids[index].ingredient_id"
                          :searchable="true"
                          :clearable="false"
                          :reduce="(ingredient) => ingredient.id"
                          label="name"
                          placeholder="Wybierz składnik"
                        >
                        </v-select>
                      </div>

                      <d-input-group @click.stop class="w-25 ml-2">
                        <d-input
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          v-model="data.fluids[index].quantity"
                        />
                        <template #append>
                          <d-input-group-text> % </d-input-group-text>
                        </template>
                      </d-input-group>
                    </div>

                    <div
                      class="block-values block-fluid-values mt-2 d-flex w-100"
                    >
                      <div @click.stop class="w-70">
                        <d-input
                          v-model="data.fluids[index].comment"
                          placeholder="Dodatkowy komentarz"
                        >
                        </d-input>
                      </div>

                      <div @click.stop class="w-30 ml-2">
                        <d-input
                          v-model="data.fluids[index].amount"
                          placeholder="Ilość"
                        />
                      </div>
                    </div>
                  </d-card-body>

                  <a
                    href="#delete"
                    class="block-remove text-white"
                    @click.stop.prevent="removeFluid(index)"
                  >
                    <i class="material-icons">delete</i>
                  </a>
                </d-card>
              </li>
            </template>
          </draggable>

          <draggable
            v-if="data.addons && data.addons.length && editView === 'addons'"
            v-model="data.addons"
            tag="ol"
            item-key="id"
            class="blocks-list mt-3"
          >
            <template #item="{ element: addon, index }">
              <li
                v-if="data.addons[index]"
                @click="toggleBlockSelection(addon.id)"
                :class="{
                  block: true,
                  'block-addon': true,
                  'is-selected': state.selectedBlock === addon.id,
                }"
              >
                <d-card>
                  <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                    <div class="block-values d-flex">
                      <div @click.stop class="form-control is-select">
                        <v-select
                          :options="ingredientsAddons"
                          v-model="data.addons[index].ingredient_id"
                          :searchable="true"
                          :clearable="false"
                          :reduce="(ingredient) => ingredient.id"
                          label="name"
                          placeholder="Wybierz dodatek"
                        >
                        </v-select>
                      </div>
                    </div>

                    <div
                      class="block-values block-fluid-values mt-2 d-flex w-100"
                    >
                      <div @click.stop class="w-70">
                        <d-input
                          v-model="data.addons[index].comment"
                          placeholder="Dodatkowy komentarz"
                        >
                        </d-input>
                      </div>

                      <div @click.stop class="w-30 ml-2">
                        <d-input
                          v-model="data.addons[index].amount"
                          placeholder="Ilość"
                        />
                      </div>
                    </div>
                  </d-card-body>

                  <a
                    href="#delete"
                    class="block-remove text-white"
                    @click.stop.prevent="removeAddon(index)"
                  >
                    <i class="material-icons">delete</i>
                  </a>
                </d-card>
              </li>
            </template>
          </draggable>

          <draggable
            v-if="data.layers && data.layers.length && editView === 'texts'"
            v-model="data.layers"
            tag="ol"
            item-key="id"
            class="blocks-list blocks-list-layers mt-3"
          >
            <template #item="{ element: block, index }">
              <li
                v-if="data.layers[index]"
                @click="toggleBlockSelection(block.id)"
                :class="{
                  block: true,
                  'is-selected': state.selectedBlock === block.id,
                }"
              >
                <d-card>
                  <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                    <div @click.stop>
                      <d-input
                        placeholder="Tekst do wyświetlenia"
                        v-model="block.text"
                        @click.stop
                      />
                    </div>

                    <div v-if="useDrinkEditor" @click.stop class="pt-3">
                      <d-button-group class="ml-0 mr-auto">
                        <d-button
                          theme="primary"
                          :outline="true"
                          @click.stop.prevent="decreaseFontSize(index)"
                        >
                          <i class="material-icons">remove</i>
                        </d-button>
                        <d-button
                          theme="primary"
                          :outline="true"
                          @click.stop.prevent="increaseFontSize(index)"
                        >
                          <i class="material-icons">add</i>
                        </d-button>
                      </d-button-group>

                      <d-button-group
                        @click.stop
                        :key="'btns-group-' + index"
                        class="ml-auto mr-auto"
                      >
                        <d-button
                          :key="'btn-text-color-dark-' + index"
                          theme="primary"
                          :outline="
                            !(!block.textColor || block.textColor === 'dark')
                          "
                          @click.stop.prevent="setTextColor(index, 'dark')"
                        >
                          Ciemny tekst
                        </d-button>
                        <d-button
                          :key="'btn-text-color-light-' + index"
                          theme="primary"
                          :outline="block.textColor !== 'light'"
                          @click.stop.prevent="setTextColor(index, 'light')"
                        >
                          Jasny tekst
                        </d-button>
                      </d-button-group>

                      <d-button-group class="ml-auto">
                        <d-button
                          theme="primary"
                          :outline="block.textAlign !== 'left'"
                          @click.stop.prevent="block.textAlign = 'left'"
                        >
                          <i class="material-icons">format_align_left</i>
                        </d-button>
                        <d-button
                          theme="primary"
                          :outline="block.textAlign !== 'center'"
                          @click.stop.prevent="block.textAlign = 'center'"
                        >
                          <i class="material-icons">format_align_center</i>
                        </d-button>
                        <d-button
                          theme="primary"
                          :outline="block.textAlign !== 'right'"
                          @click.stop.prevent="block.textAlign = 'right'"
                        >
                          <i class="material-icons">format_align_right</i>
                        </d-button>
                      </d-button-group>
                    </div>
                  </d-card-body>

                  <a
                    href="#delete"
                    class="block-remove text-white"
                    @click.stop.prevent="removeLayer(index)"
                  >
                    <i class="material-icons">delete</i>
                  </a>
                </d-card>
              </li>
            </template>
          </draggable>

          <draggable
            v-if="data.steps && data.steps.length && editView === 'steps'"
            v-model="data.steps"
            tag="ol"
            item-key="id"
            class="blocks-list mt-3"
          >
            <template #item="{ index }">
              <li v-if="data.steps[index]" :class="{ block: true, 'block-step': true }">
                <d-card>
                  <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                    <div v-if="data.steps[index]" class="block-values d-flex">
                      <h5>
                        Krok {{ index + 1 }}

                        <a
                          href="#delete"
                          class="block-remove text-white"
                          @click.stop.prevent="removeStep(index)"
                        >
                          <i class="material-icons">delete</i>
                        </a>
                      </h5>

                      <div @click.stop class="form-control is-select">
                        <v-select
                          :options="drinkIngredients"
                          v-model="data.steps[index].ingredient1_id"
                          :searchable="true"
                          :clearable="false"
                          :reduce="(ingredient) => ingredient.id"
                          label="name"
                          placeholder="Wybierz pierwszy składnik"
                        >
                        </v-select>
                      </div>

                      <div @click.stop class="form-control is-select mt-2">
                        <v-select
                          :options="drinkIngredients"
                          v-model="data.steps[index].ingredient2_id"
                          :searchable="true"
                          :clearable="true"
                          :reduce="(ingredient) => ingredient.id"
                          label="name"
                          placeholder="Wybierz drugi składnik (opcjonalnie)"
                        >
                        </v-select>
                      </div>
                    </div>

                    <div
                      v-if="
                        data.steps[index] &&
                        data.steps[index].ingredient1_id &&
                        data.steps[index].ingredient2_id
                      "
                      class="block-values block-fluid-values mt-2 d-flex w-100"
                    >
                      <d-input
                        v-model="data.steps[index].comment"
                        placeholder="Dodatkowy komentarz"
                      >
                      </d-input>
                    </div>
                  </d-card-body>
                </d-card>
              </li>
            </template>
          </draggable>
        </d-col>

        <d-col :cols="useDrinkEditor ? 7 : 8">
          <buildup
            v-if="!useDrinkEditor && allDataLoaded"
            :blocks="data.blocks"
            :editView="editView"
            :ingredientsData="ingredientsFood"
            :layers="data.layers"
            :selectedBlock="state.selectedBlock"
            :units="units"
          />
          <buildup-drinks
            v-if="useDrinkEditor && allDataLoaded"
            ref="buildup-drinks"
            :fluids="data.fluids"
            :addons="data.addons"
            :editView="editView"
            :ingredientsData="ingredientsDrinks"
            :ingredientsAddonsData="ingredientsAddons"
            :layers="data.layers"
            :drinkSize="data.size"
            :selectedBlock="state.selectedBlock"
            :fluidQuantities="fluidsQuantities"
            :coverIngredient="data.cover_ingredient"
            :toppingIngredient="data.topping_ingredient"
            :usesCoverIngredient="data.use_cover_ingredient"
            :usesToppingIngredient="data.use_topping_ingredient"
            :fluidParams="{
              diffTopBottom: 0.2,
              padding: 50,
              width: 240,
              height: 390,
            }"
          />
        </d-col>
      </d-row>

      <product-form-comments v-if="allDataLoaded" :productID="data.id" />
    </d-form>
  </d-container>
</template>

<script src="./script.js"></script>
<style src="./style.scss" lang="scss" scoped></style>
